
import { useEffect, useState } from 'react'
import { Icon } from '@/components/ui/Icon'
import { formatPackLine, type PackList } from '@/lib/cables'
import { SIGNAL_META } from '@/lib/signals'
import { SDI_RATE_LABELS, formatByKey } from '@/lib/formats'
import { useUiMode } from '@/components/workspace/UiModeProvider'
import { term } from '@/lib/ui-mode'
import { useModal } from '@/lib/use-modal'
import type { CapabilityReport, LinkFinding } from '@/lib/capability'
import type { SignalCategory } from '@/lib/signals'
import { btn, cx, glass, notice, segment } from '@/lib/ui'

type Spares = 'none' | 'each' | 'percent'

/**
 * What to put in the van.
 *
 * Grouped by cable type and length, because that is the unit a crew packs in —
 * nobody loads "the run from CAM-3 to the switcher", they load four twenty-metre
 * SDI leads. Lengths are rounded up to sizes you can actually get hold of.
 *
 * Opens over the canvas rather than navigating away: the moment you want this
 * is while you are looking at the diagram, and the canvas has unsaved state.
 */
export function PackListPanel({
  onClose,
  apiBase,
  projectId,
  projectName,
  showFormat,
}: {
  onClose: () => void
  apiBase: string
  projectId: string
  projectName: string
  /** Included so a format change re-runs the reach maths straight away. */
  showFormat: string | null
}) {
  const { advanced } = useUiMode()
  const [spares, setSpares] = useState<Spares>('none')
  const [list, setList] = useState<PackList | null>(null)
  const [capability, setCapability] = useState<CapabilityReport | null>(null)
  const [links, setLinks] = useState<LinkFinding[]>([])
  // Starts loading rather than flipping to it inside the effect: the panel is
  // mounted fresh on each open, so there is no earlier state to correct.
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('loading')

  useEffect(() => {
    let cancelled = false
    fetch(`${apiBase}/projects/${projectId}/cables?spares=${spares}`)
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('failed'))))
      .then((data: { packList: PackList; capability: CapabilityReport; links: LinkFinding[] }) => {
        if (!cancelled) {
          setList(data.packList)
          setCapability(data.capability)
          setLinks(data.links ?? [])
          setState('idle')
        }
      })
      .catch(() => !cancelled && setState('error'))
    return () => {
      cancelled = true
    }
  }, [spares, apiBase, projectId, showFormat])

  // Escape, the focus trap and the return of focus to the toolbar button that
  // opened this all come from the shared hook. Before it, Tab walked straight
  // out of the panel onto a canvas the reader could not see, and closing left
  // focus on the document.
  const dialogRef = useModal<HTMLDivElement>({ onClose })

  return (
    <div className="absolute inset-0 z-40 flex items-start justify-center bg-[var(--scrim)] p-4 backdrop-blur-[2px] sm:p-8">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pack-list-title"
        className={cx(glass, 'flex max-h-full w-full max-w-2xl flex-col overflow-hidden')}
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--border)] px-5 py-3.5">
          <div>
            <h2 id="pack-list-title" className="text-sm font-medium">{term(advanced, 'packList')}</h2>
            <p className="text-[11px] text-muted">
              {projectName}
              {advanced &&
                (showFormat
                  ? ` · ${formatByKey(showFormat)?.label ?? showFormat}`
                  : ' · no show format set')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`${apiBase}/projects/${projectId}/pack-list.csv?spares=${spares}`}
              className={cx(btn.sm, btn.secondary)}
            >
              <Icon name="sheet" className="h-3.5 w-3.5" />
              CSV
            </a>
            <button type="button" onClick={onClose} aria-label="Close pack list" className={cx(btn.sm, btn.ghost)}>
              <Icon name="close" className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-[var(--border)] px-5 py-2.5">
          <span className="text-[11px] text-muted">Spares</span>
          {(
            [
              ['none', 'None'],
              ['each', '+1 each type'],
              ['percent', '+10%'],
            ] as [Spares, string][]
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setSpares(value)}
              aria-pressed={spares === value}
              className={cx(segment.base, spares === value ? segment.on : segment.off)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {state === 'loading' && <p className="text-sm text-muted">Working it out…</p>}
          {state === 'error' && <p role="alert" className={notice.error}>Could not build the pack list.</p>}

          {list && state !== 'loading' && (
            <>
              {list.groups.length === 0 && list.unmeasured.length === 0 && (
                <p className="py-8 text-center text-sm text-muted">
                  No cables drawn yet.
                </p>
              )}

              {capability && capability.tooSlow.length > 0 && (
                <div role="alert" className={cx(notice.error, 'mb-4')}>
                  <p className="font-medium">
                    {capability.tooSlow.length}{' '}
                    {capability.tooSlow.length === 1 ? 'device cannot' : 'devices cannot'} run this
                    show format
                  </p>
                  {/* The warning fires in both modes, but simple mode used to
                      answer it with a bare rate key — "tops out at 3G" — which
                      is the code, not the problem. Same warning, said in a way
                      that names what to do about it. */}
                  <ul className="mt-1.5 space-y-1 text-[11px]">
                    {capability.tooSlow.map((device) => (
                      <li key={device.label}>
                        <span className="font-medium">{device.label}</span>{' '}
                        {advanced ? (
                          <>
                            tops out at {device.deviceRateLabel}, and this show needs{' '}
                            {device.rateLabel}.
                          </>
                        ) : (
                          <>
                            cannot handle {showFormat ?? 'this picture format'}. Swap it for one that
                            can, or run the show at a lower format.
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Shown in BOTH modes on purpose. Simple hides controls, never
                  consequences, and this one needs no control at all: it comes
                  from the two devices a cable already joins, so a podcaster who
                  never meets the show-format picker still gets the answer. */}
              {links.length > 0 && (
                <div className={cx(notice.warning, 'mb-4')}>
                  <p className="font-medium">
                    {links.length} {links.length === 1 ? 'run loses' : 'runs lose'} picture on the
                    way through
                  </p>
                  <ul className="mt-1.5 space-y-1 text-[11px]">
                    {links.map((link) => (
                      <li key={link.runId}>
                        <span className="font-medium">{link.label}</span> —{' '}
                        {link.kind === 'CAPTURE' ? 'records at' : 'only accepts'}{' '}
                        {link.sinkFormat}, but the source puts out {link.sourceFormat}. You lose{' '}
                        {link.resolution && link.frameRate
                          ? 'resolution and frame rate'
                          : link.resolution
                            ? 'resolution'
                            : 'frame rate'}
                        .
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Named, not assumed fine. A device nobody has researched is an
                  open question, and saying so is the difference between a
                  clean screen and a promise nobody made. */}
              {advanced && capability && capability.unknown.length > 0 && (
                <p className="mb-4 text-[11px] text-muted">
                  {capability.unknown.length} device
                  {capability.unknown.length === 1 ? ' has' : 's have'} no researched format
                  capability yet, so {capability.unknown.length === 1 ? 'it is' : 'they are'} not
                  checked: {capability.unknown.slice(0, 4).join(', ')}
                  {capability.unknown.length > 4 && ` and ${capability.unknown.length - 4} more`}.
                </p>
              )}

              {list.groups.map((group) => (
                <section key={group.type.key} className="mb-4 last:mb-0">
                  <div className="flex items-baseline gap-2">
                    <span
                      aria-hidden
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: SIGNAL_META[group.type.signal as SignalCategory]?.color }}
                    />
                    <h3 className="text-sm font-medium">{group.type.label}</h3>
                    <span className="text-[11px] text-muted">
                      {group.cables + group.spares} total · {Math.round(group.metres)} m
                    </span>
                  </div>
                  <ul className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1 pl-4">
                    {group.lines.map((line) => (
                      <li key={line.lengthM} className="text-sm tabular-nums">
                        {formatPackLine(line)}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}

              {list.groups.length > 0 && (
                <p className="mt-4 border-t border-[var(--border)] pt-3 text-sm">
                  <strong className="font-medium">
                    {list.totalCables + list.totalSpares} cables
                  </strong>{' '}
                  <span className="text-muted">
                    · {Math.round(list.totalMetres)} m total
                    {list.totalSpares > 0 && ` · includes ${list.totalSpares} spare`}
                  </span>
                </p>
              )}

              {/* Named, not counted. A pack list that quietly omitted these
                  would send a crew out short, which is the whole point of it. */}
              {list.unmeasured.length > 0 && (
                <div className={cx(notice.warning, 'mt-4')}>
                  <p className="font-medium">
                    {list.unmeasured.length}{' '}
                    {list.unmeasured.length === 1 ? 'run has' : 'runs have'} no length yet, so{' '}
                    {list.unmeasured.length === 1 ? 'it is' : 'they are'} not counted above.
                  </p>
                  <ul className="mt-1.5 space-y-0.5 text-[11px]">
                    {list.unmeasured.slice(0, 8).map((run) => (
                      <li key={run.id}>
                        {run.label} <span className="opacity-70">· {run.typeLabel}</span>
                      </li>
                    ))}
                    {list.unmeasured.length > 8 && (
                      <li className="opacity-70">and {list.unmeasured.length - 8} more</li>
                    )}
                  </ul>
                  <p className="mt-1.5 text-[11px] opacity-80">
                    Select a device and use &ldquo;Set all&rdquo; to fill a whole position at once.
                  </p>
                </div>
              )}

              {list.tooLong.length > 0 && (
                <div role="alert" className={cx(notice.error, 'mt-3')}>
                  <p className="font-medium">
                    {list.tooLong.length}{' '}
                    {list.tooLong.length === 1 ? 'run is' : 'runs are'} longer than that cable is
                    made in
                  </p>
                  <ul className="mt-1.5 space-y-1 text-[11px]">
                    {list.tooLong.map((run) => (
                      <li key={run.id}>
                        <span className="font-medium">{run.label}</span> — {run.lengthM} m, but{' '}
                        {run.product} is only sold up to {run.longestM} m. Not counted above.
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {list.warnings.length > 0 && (
                <div role="alert" className={cx(notice.error, 'mt-3')}>
                  <p className="font-medium">
                    {list.warnings.reduce((sum, w) => sum + w.count, 0)}{' '}
                    {list.warnings.reduce((sum, w) => sum + w.count, 0) === 1 ? 'run is' : 'runs are'}{' '}
                    past the usual reach for that cable
                  </p>
                  <ul className="mt-1.5 space-y-1 text-[11px]">
                    {list.warnings.map((warning) => (
                      <li key={warning.runIds[0]}>
                        <span className="font-medium">
                          {warning.count > 1 && `${warning.count} × `}
                          {warning.runLabel}
                        </span>{' '}
                        — {warning.lengthM} m, which is longer than this cable reliably carries
                        (about {warning.limitM} m).
                        {advanced && (
                          <>
                            {' '}
                            {warning.typeLabel}
                            {warning.rate ? `, ${SDI_RATE_LABELS[warning.rate]}` : ''}.{' '}
                            <span className="opacity-80">{warning.note}</span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
